export type FreshnessLevel = 'fresh' | 'recent' | 'stale' | 'old';

export interface FreshnessInfo {
  level: FreshnessLevel;
  minutes: number;
  label: string;
  /** Hex color usable in map markers / info windows */
  color: string;
}

export const getFreshnessLevel = (minutes: number): FreshnessLevel => {
  if (minutes < 15) return 'fresh';
  if (minutes < 60) return 'recent';
  if (minutes < 240) return 'stale';
  return 'old';
};

export const FRESHNESS_COLORS: Record<FreshnessLevel, string> = {
  fresh: '#10b981',
  recent: '#eab308',
  stale: '#f97316',
  old: '#94a3b8',
};

export const minutesSince = (value: string | number | Date): number => {
  const date = value instanceof Date ? value : new Date(value);
  return Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
};

/** "hace 4 min" / "4 min ago" */
export const formatTimeAgo = (
  value: string | number | Date,
  language: string = 'en'
): string => {
  const mins = minutesSince(value);
  const es = language === 'es';

  if (mins < 1) return es ? 'Justo ahora' : 'Just now';
  if (mins < 60) return es ? `hace ${mins} min` : `${mins} min ago`;

  const hours = Math.floor(mins / 60);
  if (hours < 24) return es ? `hace ${hours} h` : `${hours} h ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return es ? 'hace 1 día' : '1 day ago';
  return es ? `hace ${days} días` : `${days} days ago`;
};

/** "Actualizado hace 4 min" / "Updated 4 min ago" */
export const formatLastUpdated = (
  value: string | number | Date | null | undefined,
  language: string = 'en'
): string => {
  const es = language === 'es';
  if (!value) return es ? 'Sin datos aún' : 'No data yet';
  return `${es ? 'Actualizado' : 'Updated'} ${formatTimeAgo(value, language)}`;
};

export const getFreshnessInfo = (
  value: string | number | Date,
  language: string = 'en'
): FreshnessInfo => {
  const minutes = minutesSince(value);
  const level = getFreshnessLevel(minutes);
  return {
    level,
    minutes,
    label: formatTimeAgo(value, language),
    color: FRESHNESS_COLORS[level],
  };
};

/** Human description of how trustworthy the data age is */
export const getFreshnessDescription = (
  level: FreshnessLevel,
  language: string = 'en'
): string => {
  const es = language === 'es';
  switch (level) {
    case 'fresh':
      return es ? 'Información reciente' : 'Recent information';
    case 'recent':
      return es ? 'Puede haber cambiado' : 'May have changed';
    case 'stale':
      return es ? 'Información antigua' : 'Outdated information';
    default:
      return es ? 'Muy antigua' : 'Very old';
  }
};
